import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import { formatCount, getCategoryName, getRelatedVideos, getVideoById } from '@/utils/video';

function durationToSeconds(d: string): number {
  const [m, s] = d.split(':').map(Number);
  return m * 60 + s;
}

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function DetailPage() {
  const { videoId = '' } = useParams();
  const video = getVideoById(videoId);

  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [liked, setLiked] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState('');

  usePageMeta({
    title: video ? `${video.title} - 抖音短视频` : '视频详情 - 抖音短视频',
    description: video
      ? `由 ${video.author} 发布的短视频「${video.title}」。${video.description.slice(0, 60)}`
      : '抖音短视频详情页，观看精彩内容。',
    keywords: video
      ? `${video.title},${video.author},${getCategoryName(video.category)},抖音短视频`
      : '抖音短视频',
  });

  const total = video ? durationToSeconds(video.duration) : 0;

  useEffect(() => {
    if (!playing) return;
    const id = window.setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          setPlaying(false);
          return 100;
        }
        return p + 100 / (total * 10);
      });
    }, 100);
    return () => window.clearInterval(id);
  }, [playing, total]);

  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(''), 2000);
    return () => window.clearTimeout(id);
  }, [toast]);

  const showToast = (msg: string) => setToast(msg);

  const handleShareCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast('链接已复制，快去分享给朋友吧');
    } catch {
      showToast('链接：' + window.location.href);
    }
    setShareOpen(false);
  };

  if (!video) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Header />
        <main className="flex-1 flex flex-col items-center justify-center text-center px-4 pt-24">
          <span className="w-16 h-16 rounded-full bg-secondary-100 text-secondary-400 flex items-center justify-center mb-4">
            <i className="ri-video-off-line text-3xl"></i>
          </span>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">视频走丢了</h1>
          <p className="text-sm text-foreground-500 mb-6">这个视频可能已被删除或链接有误</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold cursor-pointer"
          >
            <i className="ri-home-5-line"></i>
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = getRelatedVideos(video.category, video.id, 6);
  const likeCount = video.likes + (liked ? 1 : 0);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />
      <main className="flex-1 pt-16">
        <div className="w-full px-4 md:px-8 py-6">
          {/* 面包屑 */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5 flex-wrap" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600 cursor-pointer flex items-center gap-1">
              <i className="ri-home-4-line"></i>
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${video.category}`} className="hover:text-primary-600 cursor-pointer">
              {getCategoryName(video.category)}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 line-clamp-1">{video.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* 左侧：播放器 + 信息 */}
            <div className="lg:col-span-2">
              {/* 播放器 */}
              <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-foreground-950">
                <img
                  src={video.cover}
                  alt={`${video.title} - 抖音短视频`}
                  title={`${video.title} - 抖音短视频`}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-foreground-950/50"></div>

                {/* 演示标注 */}
                <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-[11px] font-medium flex items-center gap-1">
                  <i className="ri-information-line"></i>
                  演示模式 · 未接入真实视频流
                </span>

                {/* 播放按钮 */}
                <button
                  type="button"
                  aria-label={playing ? '暂停' : '播放'}
                  onClick={() => {
                    if (progress >= 100) setProgress(0);
                    setPlaying((v) => !v);
                  }}
                  className="absolute inset-0 flex items-center justify-center cursor-pointer group"
                >
                  <span className="w-20 h-20 rounded-full bg-background-50/20 backdrop-blur flex items-center justify-center border border-background-50/40 group-hover:bg-background-50/30 transition-colors">
                    <i className={playing ? 'ri-pause-fill text-4xl text-background-50' : 'ri-play-fill text-4xl text-background-50'}></i>
                  </span>
                </button>

                {/* 底部控制条 */}
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-foreground-950/80 to-transparent">
                  <div className="h-1 rounded-full bg-background-50/30 overflow-hidden">
                    <div className="h-full bg-primary-500 transition-all" style={{ width: `${progress}%` }}></div>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-background-100 font-label">
                    <span>{formatTime((progress / 100) * total)} / {video.duration}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {formatCount(video.plays)} 次播放
                    </span>
                  </div>
                </div>
              </div>

              {/* 标题信息 */}
              <h1 className="mt-5 text-xl md:text-2xl font-bold text-foreground-950 leading-snug">{video.title}</h1>

              <div className="mt-3 flex items-center flex-wrap gap-x-4 gap-y-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
                    <i className="ri-user-3-line text-xs"></i>
                  </span>
                  <span className="text-foreground-800 font-medium">{video.author}</span>
                </span>
                <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-800">{video.authorTag}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  发布于 {video.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {video.duration}
                </span>
              </div>

              {/* 操作按钮 */}
              <div className="mt-5 flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => setLiked((v) => !v)}
                  className={`inline-flex items-center gap-2 h-10 px-5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    liked
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
                  }`}
                >
                  <i className={liked ? 'ri-heart-3-fill text-base' : 'ri-heart-3-line text-base'}></i>
                  {liked ? '已点赞' : '点赞'} · {formatCount(likeCount)}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-secondary-100 text-secondary-900 hover:bg-secondary-200 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line text-base"></i>
                  分享
                </button>
                <Link
                  to={`/category/${video.category}`}
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-secondary-100 text-secondary-900 hover:bg-secondary-200 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-folder-3-line text-base"></i>
                  {getCategoryName(video.category)}
                </Link>
              </div>

              {/* 简介 */}
              <div className="mt-6 rounded-lg bg-background-100/70 border border-background-200/70 p-5">
                <h2 className="text-sm font-bold text-foreground-900 mb-2">视频简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {video.tags.map((t) => (
                    <Link
                      key={t}
                      to={`/search?q=${encodeURIComponent(t)}`}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium cursor-pointer hover:bg-secondary-200 transition-colors flex items-center gap-1"
                    >
                      <i className="ri-price-tag-3-line"></i>
                      {t}
                    </Link>
                  ))}
                </div>
              </div>

              <p className="mt-4 text-xs text-foreground-400 flex items-center gap-1.5">
                <i className="ri-information-line"></i>
                本站为内容展示演示平台，播放、点赞与分享均为前端演示效果，未接入真实视频流与统计数据。
              </p>
            </div>

            {/* 右侧：相关推荐 */}
            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-20">
                <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-film-line text-primary-500"></i>
                  相关推荐
                </h2>
                <div className="space-y-3">
                  {related.map((v) => (
                    <Link
                      key={v.id}
                      to={`/detail/${v.id}`}
                      className="group flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="relative w-28 h-20 rounded-md overflow-hidden bg-secondary-100 shrink-0">
                        <img
                          src={v.cover}
                          alt={`${v.title} - 抖音短视频`}
                          title={`${v.title} - 抖音短视频`}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-foreground-950/60 text-background-50 text-[10px] font-label">
                          {v.duration}
                        </span>
                      </div>
                      <div className="min-w-0 py-0.5">
                        <h3 className="text-sm text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {v.title}
                        </h3>
                        <p className="text-xs text-foreground-400 mt-1.5 flex items-center gap-3">
                          <span className="truncate">{v.author}</span>
                          <span className="flex items-center gap-1 shrink-0">
                            <i className="ri-heart-3-line text-primary-400"></i>
                            {formatCount(v.likes)}
                          </span>
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />

      {/* 分享弹窗 */}
      {shareOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setShareOpen(false)}
          ></div>
          <div className="relative w-full max-w-sm bg-background-50 rounded-lg p-6 animate-fade-up">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-bold text-foreground-950">分享这个视频</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setShareOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-background-100 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-xl text-foreground-600"></i>
              </button>
            </div>
            <p className="text-sm text-foreground-500 mb-5">选择一种方式，把精彩分享出去</p>
            <div className="grid grid-cols-4 gap-3 mb-5">
              {[
                { icon: 'ri-link', label: '复制链接', action: 'copy' },
                { icon: 'ri-wechat-line', label: '微信', action: 'wx' },
                { icon: 'ri-weibo-line', label: '微博', action: 'wb' },
                { icon: 'ri-more-line', label: '更多', action: 'more' },
              ].map((s) => (
                <button
                  key={s.action}
                  type="button"
                  onClick={() => {
                    if (s.action === 'copy') handleShareCopy();
                    else {
                      showToast('演示模式：暂未接入第三方分享');
                      setShareOpen(false);
                    }
                  }}
                  className="flex flex-col items-center gap-2 py-3 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer"
                >
                  <span className="w-11 h-11 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
                    <i className={`${s.icon} text-xl`}></i>
                  </span>
                  <span className="text-xs text-foreground-600">{s.label}</span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-background-100 text-xs text-foreground-500">
              <i className="ri-links-line shrink-0"></i>
              <span className="truncate">{window.location.href}</span>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-full bg-foreground-950 text-background-50 text-sm animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}